/** The products this platform asks for, and what state each one is in. */
import { useState, type FormEvent } from "react";
import { ApiError, api } from "../../lib/api.ts";
import { Link, useRouter } from "../../lib/router.tsx";
import { useAsync } from "../../lib/useAsync.ts";
import { useAuth } from "../../lib/auth.tsx";
import { Loading, PageHead, Problem, Tiles } from "../../components/Page.tsx";

export interface ProductSummary {
  id: string;
  code: string;
  name: string;
  lineOfBusiness: string;
  description: string | null;
  liveVersion: number | null;
  draftVersion: number | null;
  fieldCount: number;
}

export function Products() {
  const { can } = useAuth();
  const products = useAsync(() => api.get<{ products: ProductSummary[] }>("/api/products"), []);
  const [adding, setAdding] = useState(false);

  const rows = products.data?.products ?? [];

  return (
    <div className="stack">
      <PageHead
        title="Products"
        sub="A product is a selection of library fields, arranged into sections and frozen at a version."
        actions={can("products", "edit") && !adding && (
          <button className="btn btn-primary" type="button" onClick={() => setAdding(true)}>New product</button>
        )}
      />

      <Tiles
        tiles={[
          { label: "Products", value: rows.length, tone: "accent" },
          { label: "Live", value: rows.filter((row) => row.liveVersion).length, tone: "ok" },
          { label: "In draft", value: rows.filter((row) => row.draftVersion).length, tone: "warn" },
          { label: "Questions asked", value: rows.reduce((total, row) => total + row.fieldCount, 0), tone: "info" },
        ]}
      />

      {adding && <NewProduct onDone={() => { setAdding(false); products.reload(); }} onCancel={() => setAdding(false)} />}

      <Problem error={products.error} />

      <div className="card">
        {products.loading && !products.data ? (
          <Loading what="products" />
        ) : rows.length === 0 ? (
          <div className="empty">
            <h3>No products yet</h3>
            <p className="small">Build one from the fields in the library. It takes minutes, and no deployment.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Product</th><th>Code</th><th>Line of business</th><th>Fields</th><th>State</th></tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/products/${row.code}`}>{row.name}</Link>
                      {row.description && <div className="small muted truncate" style={{ maxWidth: 320 }}>{row.description}</div>}
                    </td>
                    <td><span className="key">{row.code}</span></td>
                    <td>{row.lineOfBusiness}</td>
                    <td>{row.fieldCount || <span className="muted">—</span>}</td>
                    <td>
                      <div className="chips">
                        {row.liveVersion
                          ? <span className="chip chip-ok">Live · v{row.liveVersion}</span>
                          : <span className="chip">Not published</span>}
                        {row.draftVersion && <span className="chip chip-warn">Draft · v{row.draftVersion}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function NewProduct({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { navigate } = useRouter();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [lineOfBusiness, setLineOfBusiness] = useState("");
  const [description, setDescription] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const derived = code.trim() || name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    try {
      await api.post("/api/products", {
        code: derived, name: name.trim(), lineOfBusiness: lineOfBusiness.trim(), description: description.trim() || null,
      });
      onDone();
      navigate(`/products/${derived}`);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not create this product.");
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <div className="card-head"><h2>New product</h2></div>
      <div className="card-body stack">
        {problem && <div className="notice notice-bad">{problem}</div>}
        <div className="field-row">
          <div className="field">
            <label htmlFor="product-name">Name<span className="required-mark">*</span></label>
            <input id="product-name" className="control" value={name} required autoFocus
              placeholder="General Liability" onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="product-code">Code</label>
            <input id="product-code" className="control" value={code} placeholder={derived || "general_liability"}
              onChange={(event) => setCode(event.target.value)} />
            <span className="hint">Permanent, and how other systems will refer to this product.</span>
          </div>
          <div className="field">
            <label htmlFor="product-lob">Line of business<span className="required-mark">*</span></label>
            <input id="product-lob" className="control" value={lineOfBusiness} required
              placeholder="General Liability" onChange={(event) => setLineOfBusiness(event.target.value)} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="product-description">Description</label>
          <input id="product-description" className="control" value={description}
            onChange={(event) => setDescription(event.target.value)} />
        </div>
      </div>
      <div className="card-foot">
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Creating…" : "Create product"}</button>
      </div>
    </form>
  );
}
